import type { components } from '../api/schema.gen.js';

/**
 * The permission map the server returns at sign-in, mirrored from the
 * generated OpenAPI types.
 *
 * The UI never decides what a user may do — the server already decided, and
 * `GET /auth/session` reports the answer. Client-side checks exist only to
 * hide controls that would fail, which is Nielsen's error-prevention
 * heuristic: the best error message is the one for an action the user was
 * never offered.
 *
 * This is presentation, not security. Every action is enforced again on the
 * server, so hiding a button is a courtesy, never a control.
 */
export type PermissionMap = components['schemas']['PermissionMap'];
export type Permission = keyof PermissionMap;
export type Scope = NonNullable<PermissionMap[Permission]>;

const SCOPE_RANK: Record<Scope, number> = { own: 1, team: 2, org: 3 };

/** Does the map grant `permission` at `required` scope or wider? */
export function hasPermission(
  permissions: PermissionMap | undefined,
  permission: Permission,
  required: Scope = 'own',
): boolean {
  const granted = permissions?.[permission];
  if (!granted) return false;
  return SCOPE_RANK[granted] >= SCOPE_RANK[required];
}

/** True if any of the permissions is held. For menu sections with several entries. */
export function hasAnyPermission(
  permissions: PermissionMap | undefined,
  candidates: readonly Permission[],
  required: Scope = 'own',
): boolean {
  return candidates.some((permission) => hasPermission(permissions, permission, required));
}
